import debounce from 'debounce'
import { useSelect } from 'downshift'
import { memo, useEffect, useRef, useState } from 'react'
import isEqual from 'react-fast-compare'

import { generateBidIncrements } from '@bonhams1793/bonhams-typescript'

import { decodeHtml } from '@nx/decode-html'
import { buildPriceString } from '@nx/helpers'
import { getTranslate } from '@nx/translations'

import { getBidIncrements } from '../../helpers/getBidIncrements/getBidIncrements'
import {
  StyledBody,
  StyledChevronIcon,
  StyledContainer,
  StyledInput,
  StyledList,
  StyledListContainer,
  StyledListItem,
} from './BiddingDropdown.styles'
import translate from './BiddingDropdown.translate.json'

const { text } = getTranslate(translate)

export const BiddingDropdown = memo(
  ({
    biddingPolicy,
    currencySymbol,
    currentBid,
    nextBid,
    maxBidValue,
    maxPossibleBidValue,
    numberOfOptions,
    selectBidCallback,
    isCurrentBidIncluded = false,
    label,
    isWinningBidder,
    hasError,
    ...props
  }: BiddingDropdownProps) => {
    const dropdownContainerRef = useRef<HTMLDivElement>(null)
    const [items, setItems] = useState<number[]>([])

    const getIsItemDisabled = (item: number) => {
      return item < currentBid || item === maxBidValue
    }

    const initialBidValue =
      isWinningBidder && maxBidValue && maxBidValue > currentBid
        ? currentBid
        : isCurrentBidIncluded
          ? nextBid
          : undefined

    const {
      isOpen,
      selectedItem,
      getToggleButtonProps,
      getLabelProps,
      getMenuProps,
      getItemProps,
      selectItem,
    } = useSelect({
      items: items,
      onIsOpenChange: (payload) => {
        if (payload.isOpen) {
          if (items[0] === undefined || items[0] !== initialBidValue) {
            setItems([
              ...(initialBidValue ? [initialBidValue] : []),
              ...getBidIncrements({
                biddingPolicy,
                currentBidValue: currentBid,
                isWinningBidder,
                maxBidValue,
                maxPossibleBidValue,
                numberOfOptions,
              }),
            ])
          }
        }
      },
      onSelectedItemChange: (payload) => {
        if (payload.selectedItem) {
          selectBidCallback(payload.selectedItem)
        }
      },
      isItemDisabled: getIsItemDisabled,
    })

    useEffect(() => {
      selectItem(nextBid)
    }, [selectItem, nextBid])

    useEffect(() => {
      const listElement = dropdownContainerRef.current

      const lazyLoadBidValues: EventListener = debounce((event: Event) => {
        const list = event.target as HTMLDivElement

        const bottomMarginTrigger = list.scrollHeight - list.clientHeight * 2

        if (list.scrollTop >= bottomMarginTrigger) {
          setItems((state) => [
            ...state,
            ...generateBidIncrements({
              policy: biddingPolicy,
              currentBid: state[state.length - 1],
              amountOfOptions: numberOfOptions,
            }),
          ])
        }
      }, 50)

      if (listElement && !maxPossibleBidValue) {
        listElement.addEventListener('scroll', lazyLoadBidValues)
      }

      return () => {
        if (listElement && !maxPossibleBidValue) {
          listElement.removeEventListener('scroll', lazyLoadBidValues)
        }
      }
    }, [
      biddingPolicy,
      numberOfOptions,
      dropdownContainerRef,
      isOpen,
      maxPossibleBidValue,
    ])

    return (
      <StyledContainer {...props}>
        <StyledBody $level="S" as="label" {...getLabelProps()}>
          {label || text('label')}
        </StyledBody>
        <StyledInput {...getToggleButtonProps()} $hasError={hasError}>
          {buildPriceString({
            currencySymbol: decodeHtml(currencySymbol),
            lowPrice: selectedItem ? selectedItem : nextBid,
          })}
          <StyledChevronIcon $direction={isOpen ? 'up' : 'down'} />
        </StyledInput>

        <StyledListContainer {...getMenuProps()} $visible={isOpen}>
          {isOpen && (
            <StyledList ref={dropdownContainerRef}>
              {items.map((item, index) => (
                <StyledListItem
                  key={item}
                  $disabled={getIsItemDisabled(item)}
                  {...getItemProps({ item, index })}
                >
                  {buildPriceString({
                    currencySymbol: decodeHtml(currencySymbol),
                    lowPrice: item,
                  })}
                </StyledListItem>
              ))}
            </StyledList>
          )}
        </StyledListContainer>
      </StyledContainer>
    )
  },
  isEqual
)

export interface BiddingDropdownProps {
  biddingPolicy: Record<string, number[]>
  currencySymbol: string
  numberOfOptions: number
  selectBidCallback: (bid: number) => void
  maxBidValue?: number
  maxPossibleBidValue?: number
  currentBid: number
  nextBid: number
  isCurrentBidIncluded?: boolean
  label?: string
  isWinningBidder: boolean
  hasError?: boolean
}
